/**
 * Top-level App — composes providers, gates rendering on the auth
 * bootstrap, and renders the shell (Header + Sidebar) + the active
 * view. Same shape as the dashboard's App.tsx, trimmed to the admin's
 * smaller surface.
 *
 * View state lifts to <App /> (above AppProviders) so the
 * AuthProvider's bootstrap-401 routing + the HTTP client's
 * `on_unauthorized` callback both reach the same `setView`.
 *
 * Initial view resolution:
 *   - `?token=…` on the URL          → 'verify' (consume the staff link)
 *   - `?view=accounts|metrics|team`  → that view (persisted by setView)
 *   - default                        → 'accounts'
 *   - login/verify are NEVER URL-restored — they're auth-flow only
 *
 * Mobile nav drawer state also lives here so the header (hamburger
 * button) and the sidebar (drawer panel) can communicate.
 */

import { FontLoader } from '@sill/ui';
import { useEffect, useState } from 'react';

import { AppProviders } from './app/providers.js';
import { useAuth } from './app/auth-context.js';
import type { View } from './app/view-router.js';
import { ViewRouter, useViewState } from './app/view-router.js';
import { AdminHeader } from './components/shell/AdminHeader.js';
import { Sidebar } from './components/shell/Sidebar.js';
import { useC } from './lib/theme.js';
import { canView } from './lib/roles.js';
import { useAdminApi } from './api/api-context.js';

const sansFont = "'Geist', sans-serif";

const RESTORABLE_VIEWS: ReadonlySet<View> = new Set([
  'accounts',
  // Founder request 2026-10-29 — ops2026 Agencies panel.
  'agencies',
  'plans',
  // ADR-0263 S1 — add-on catalog editor + create flow.
  'addon-new',
  'metrics',
  'health',
  'testsuite',
  'test-kits',
  'install-fleet',
  'team',
  'smoke',
  'protocol-tests',
  'purge',
  'deletions',
  'jobs',
  'leads',
  'staff-log',
  'settings',
  'agent-registry',
  'trust-engine',
  'trust-disputes',
  'experiments',
  'agent-operators',
  'verified-crawlers',
  'attack-path-decisions',
] as View[]);

function initialView(): View {
  if (typeof window === 'undefined') return 'accounts';
  const url = new URL(window.location.href);
  if (url.searchParams.has('token')) return 'verify';
  const requested = url.searchParams.get('view');
  if (requested !== null && RESTORABLE_VIEWS.has(requested as View)) {
    return requested as View;
  }
  // Deep links to a specific record (restored by useViewState).
  if (
    (requested === 'account-detail' && url.searchParams.has('account'))
    || (requested === 'plan-detail' && url.searchParams.has('plan'))
    || (requested === 'addon-detail' && url.searchParams.has('addon'))
  ) {
    return requested as View;
  }
  return 'accounts';
}

function AppInner({
  view,
  setView,
  accountId,
  planId,
  addonId,
  smokeRunId,
}: {
  view: View;
  setView: ReturnType<typeof useViewState>['setView'];
  accountId: string | null;
  planId: string | null;
  addonId: string | null;
  smokeRunId: string | null;
}): JSX.Element {
  const C = useC();
  const auth = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const api = useAdminApi();
  const [badges, setBadges] = useState<Partial<Record<View, number>>>({});

  // ADR-0229 — pending agent-registry proposals as a menu badge (admin+).
  // Refreshed on navigation; any failure (incl. a 404 while the API that
  // serves it is not yet deployed) just leaves the badge off.
  const authed_role = auth.state.kind === 'authenticated' ? auth.state.staff.role : undefined;
  useEffect(() => {
    if (authed_role === undefined || !canView(authed_role, 'agent-registry')) return;
    let cancelled = false;
    api
      .getAgentRegistry()
      .then((r) => {
        if (!cancelled) setBadges((b) => ({ ...b, 'agent-registry': r.pending.length }));
      })
      .catch(() => {
        if (!cancelled) setBadges((b) => ({ ...b, 'agent-registry': 0 }));
      });
    return () => {
      cancelled = true;
    };
  }, [api, authed_role, view]);

  // ADR-0235 — open trust disputes as a menu badge (admin+). Same
  // posture as the registry badge: failure / 404 leaves it off.
  useEffect(() => {
    if (authed_role === undefined || !canView(authed_role, 'trust-disputes')) return;
    let cancelled = false;
    api
      .getTrustDisputeCount()
      .then((r) => {
        if (!cancelled) setBadges((b) => ({ ...b, 'trust-disputes': r.open_count }));
      })
      .catch(() => {
        if (!cancelled) setBadges((b) => ({ ...b, 'trust-disputes': 0 }));
      });
    return () => {
      cancelled = true;
    };
  }, [api, authed_role, view]);

  // URL + browser history are owned by useViewState (pushState per
  // navigation, popstate restores), so Back stays inside ops2026.

  // A viewer cannot open Accounts (the default landing page): send them
  // to Metrics instead of a "not available" page.
  useEffect(() => {
    if (auth.state.kind !== 'authenticated') return;
    if (view === 'accounts' && !canView(auth.state.staff.role, 'accounts')) {
      setView('metrics');
    }
  }, [auth.state, view, setView]);

  // Close the mobile drawer on any view change — `setView` from inside
  // the drawer already calls onMobileClose, but a programmatic setView
  // (auth-routing, etc.) shouldn't leave the drawer open over a new
  // view.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [view]);

  // Bootstrap splash. /verify is the exception — it must render to
  // consume the token regardless of the auth state.
  if (auth.state.kind === 'bootstrap' && view !== 'verify') {
    return (
      <div
        style={{
          background: C.bg,
          minHeight: '100vh',
          fontFamily: sansFont,
          color: C.muted,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          letterSpacing: 1.5,
        }}
      >
        Loading admin…
      </div>
    );
  }

  const showShell = view !== 'login' && view !== 'verify';
  const staff = auth.state.kind === 'authenticated' ? auth.state.staff : null;

  return (
    <div
      style={{
        background: C.bg,
        minHeight: '100vh',
        fontFamily: sansFont,
        color: C.text,
      }}
    >
      {showShell ? (
        <AdminHeader
          staff={staff}
          onSignOut={() => void auth.signOut()}
          mobileNavOpen={mobileNavOpen}
          onOpenMobileNav={() => setMobileNavOpen(true)}
          showMobileNavToggle={showShell}
        />
      ) : null}
      <div className="flex">
        {showShell ? (
          <Sidebar
            view={view}
            setView={setView}
            mobileOpen={mobileNavOpen}
            onMobileClose={() => setMobileNavOpen(false)}
            badges={badges}
          />
        ) : null}
        <main className="flex-1 min-w-0">
          <ViewRouter
            view={view}
            setView={setView}
            accountId={accountId}
            planId={planId}
            addonId={addonId}
            smokeRunId={smokeRunId}
          />
        </main>
      </div>
    </div>
  );
}

export function App(): JSX.Element {
  // `initialView()` is resolved once and frozen for the providers'
  // first render — we use its value to decide whether to skip the
  // auth bootstrap (when the SPA is mounting on the `?token=…`
  // verify callback, VerifyView owns the consume → refresh → setView
  // sequence and a racing bootstrap GET /me would route to login
  // before the cookie is set).
  const startView = initialView();
  const { view, setView, accountId, planId, addonId, smokeRunId } =
    useViewState(startView);

  return (
    <AppProviders setView={setView} skipAuthBootstrap={startView === 'verify'}>
      <FontLoader />
      <AppInner
        view={view}
        setView={setView}
        accountId={accountId}
        planId={planId}
        addonId={addonId}
        smokeRunId={smokeRunId}
      />
    </AppProviders>
  );
}
